import { Insurance } from '@/core/domain/extras/models/extras-data.model';
import { getExtrasUseCase } from '@/core/domain/extras/usecases/getExtras.usecase';
import { selectReservation } from '@/core/reservation/store/reservation.selector';
import { isValid } from '@/common/utils/validation/common.utils';
import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { catchError, of, switchMap, take } from 'rxjs';

const FALLBACK_BRANCH_ID = 4;
const FALLBACK_CATEGORY_ID = '67';

export interface CoverageLevel {
    id: string;
    insuranceId: number;
    icon: string;
    nameKey: string;
    taglineKey: string;
    featureKeys: string[];
    depositKey: string;
}

/** `null` = no incluido · `true` = incluido · `string` = incluido con detalle. */
export type CoverageValue = string | true | null;

export interface CoverageRow {
    nameKey: string;
    hintKey?: string;
    values: Record<string, CoverageValue>;
}

@Component({
    selector: 'fe-coverages',
    templateUrl: './coverages.component.html',
    styleUrls: ['./coverages.component.scss'],
})
export class CoveragesComponent implements OnInit {
    public insurances: Insurance[] = [];
    public rentDays?: number;
    public freeDays?: number;
    public loadingInsurances = true;

    constructor(
        private readonly store: Store,
        private readonly getExtrasUseCase: getExtrasUseCase,
    ) {}

    public get hasInsurances(): boolean {
        return !this.loadingInsurances && this.insurances.length > 0;
    }

    public ngOnInit(): void {
        this.loadInsurances();
    }

    private loadInsurances(): void {
        this.store
            .select(selectReservation)
            .pipe(
                take(1),
                switchMap((reservation) =>
                    this.getExtrasUseCase
                        .execute(this.buildParams(reservation))
                        .pipe(catchError(() => of(null))),
                ),
            )
            .subscribe((extras) => {
                this.insurances = extras?.data?.extras.insurances ?? [];
                this.rentDays = extras?.data?.rentDays;
                this.freeDays = extras?.data?.freeDays;
                this.loadingInsurances = false;
            });
    }

    private buildParams(reservation: any): {
        id: number;
        category?: string;
        pickupDate?: string;
        returnDate?: string;
    } {
        const id = reservation?.locationSelected;
        const category = reservation?.vehicle?.id;
        const pickupDate = reservation?.startDate;
        const returnDate = reservation?.endDate;

        if (
            isValid(id) &&
            isValid(category) &&
            isValid(pickupDate) &&
            isValid(returnDate)
        ) {
            return { id, category, pickupDate, returnDate };
        }

        return {
            id: FALLBACK_BRANCH_ID,
            category: FALLBACK_CATEGORY_ID,
            pickupDate: this.fallbackDate(1),
            returnDate: this.fallbackDate(2),
        };
    }

    private fallbackDate(daysFromNow: number): string {
        return DateTime.now()
            .plus({ days: daysFromNow })
            .set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
            .toFormat("yyyy-MM-dd'T'HH:mm:ss");
    }

    public readonly breadcrumbs = [
        {
            label: 'fe_breadcrumb_coverages',
            url: 'coverages-and-protections',
        },
    ];

    public readonly levels: CoverageLevel[] = [
        {
            id: 'premium',
            insuranceId: 5,
            icon: 'images/insurance-premium.svg',
            nameKey: 'fe_coverages_level_premium_name',
            taglineKey:
                'fe_details_step3_protection_level_big_protection_card_title',
            featureKeys: [
                'fe_coverages_feature_cdw_premium',
                'fe_coverages_feature_dp',
                'fe_coverages_feature_tpl',
                'fe_coverages_feature_pai',
                'fe_coverages_feature_rap',
            ],
            depositKey: 'fe_coverages_level_premium_deposit',
        },
        {
            id: 'broad',
            insuranceId: 6,
            icon: 'images/insurance-tall.svg',
            nameKey: 'fe_coverages_level_broad_name',
            taglineKey:
                'fe_details_step3_protection_level_medium_protection_card_title',
            featureKeys: [
                'fe_coverages_feature_cdw_broad',
                'fe_coverages_feature_tpl',
                'fe_coverages_feature_pai',
                'fe_coverages_feature_rap',
            ],
            depositKey: 'fe_coverages_level_broad_deposit',
        },
        {
            id: 'basic',
            insuranceId: 7,
            icon: 'images/insurance-low.svg',
            nameKey: 'fe_coverages_level_basic_name',
            taglineKey:
                'fe_details_step3_protection_level_minimal_protection_card_title',
            featureKeys: [
                'fe_coverages_feature_tpl',
                'fe_coverages_feature_pai',
            ],
            depositKey: 'fe_coverages_level_basic_deposit',
        },
    ];

    public readonly coverages: CoverageRow[] = [
        {
            nameKey: 'fe_coverages_item_cdw_name',
            values: {
                basic: null,
                broad: 'fe_coverages_cell_deductible_broad',
                premium: 'fe_coverages_cell_deductible_premium',
            },
        },
        {
            nameKey: 'fe_coverages_item_dp_name',
            values: { basic: null, broad: null, premium: true },
        },
        {
            nameKey: 'fe_coverages_item_tpl_name',
            hintKey: 'fe_coverages_item_tpl_hint',
            values: { basic: true, broad: true, premium: true },
        },
        {
            nameKey: 'fe_coverages_item_pai_name',
            hintKey: 'fe_coverages_item_pai_hint',
            values: { basic: true, broad: true, premium: true },
        },
        {
            nameKey: 'fe_coverages_item_rap_name',
            values: { basic: null, broad: true, premium: true },
        },
    ];

    public readonly summaryKeys = [
        'fe_coverages_summary_item_mandatory',
        'fe_coverages_summary_item_daily',
        'fe_coverages_summary_item_deposit',
        'fe_coverages_summary_item_scope',
    ];

    public getFeatures(insurance: Insurance): string[] {
        return [...(insurance.coverages ?? [])]
            .sort((a, b) => a.orden - b.orden)
            .map((coverage) => coverage.description);
    }

    public getLevelMeta(insurance: Insurance): CoverageLevel | undefined {
        return this.levels.find((level) => level.insuranceId === insurance.id);
    }

    public getIcon(insurance: Insurance): string {
        return this.getLevelMeta(insurance)?.icon ?? 'images/insurance-low.svg';
    }

    public getTaglineKey(insurance: Insurance): string | null {
        return this.getLevelMeta(insurance)?.taglineKey ?? null;
    }

    public isIncluded(row: CoverageRow, level: CoverageLevel): boolean {
        return !!row.values[level.id];
    }

    public noteKey(row: CoverageRow, level: CoverageLevel): string | null {
        const value = row.values[level.id];
        return typeof value === 'string' ? value : null;
    }

    public showCheck(row: CoverageRow, level: CoverageLevel): boolean {
        return this.isIncluded(row, level) && !this.noteKey(row, level);
    }
}
